import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        banner:
          'border border-white/50 bg-transparent text-white hover:border-white hover:bg-white/10',
        secondary:
          'bg-white text-foreground border border-border hover:bg-accent dark:bg-card dark:hover:bg-accent',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        success: 'bg-success text-success-foreground hover:bg-success/90',
        warning: 'bg-warning text-warning-foreground hover:bg-warning/90',
        // Subtle variants - lighter backgrounds with colored text
        'primary-subtle': 'bg-primary-100 text-primary hover:bg-primary-200 dark:bg-primary-950 dark:text-primary-300 dark:hover:bg-primary-900',
        'destructive-subtle': 'bg-destructive/10 text-destructive hover:bg-destructive/20',
        'success-subtle': 'bg-success/10 text-success hover:bg-success/20',
        'warning-subtle': 'bg-warning/10 text-warning hover:bg-warning/20',
        'info-subtle': 'bg-info/10 text-info hover:bg-info/20',
      },
      size: {
        default: 'h-10 px-4 py-2 min-h-[36px] touch:min-h-[44px]',
        // 36px tall and, for an icon-only caller, 40px wide. Both axes need the
        // floor: 21 of 57 `MoreIconButton` call sites pass `size="sm"`, which
        // overrides the component's own `size="icon"`, so the sm variant is a
        // dense-table action affordance in practice.
        sm: 'h-9 rounded-md px-3 touch:min-h-[44px] touch:min-w-[44px]',
        lg: 'h-11 rounded-md px-8',
        // Square, so the coarse-pointer floor has to grow BOTH axes. Straight
        // min-h/min-w (not the Switch/Checkbox reserve-then-fill overlay) — an
        // overlay would bleed out of a `py-1` DataTable action cell into the
        // neighbouring row. See docs/design-system/ui-mobile-responsive.md § 1a.
        icon: 'h-10 w-10 touch:min-h-[44px] touch:min-w-[44px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
